import { DYNAMIC_CONTENT_ATTR } from './constants';
const Parser = new DOMParser();

// Clear out any HTML elements NOT from the template <body/> or <head/>
// because dynamic content (i.e. script embeds) can manipulate the DOM.
// All dynamically added nodes should be flagged with the
// DYNAMIC_CONTENT_ATTR by the MutationObserver handler.
export const cleanupDOMCruft = () => {
  const nodesToRemove = document.querySelectorAll(`[${DYNAMIC_CONTENT_ATTR}="true"]`);
  Array.from(nodesToRemove).forEach(node => {
    if (node.parentElement && node.id !== 'hs-embed-content') {
      node.parentElement.removeChild(node);
    }
  });

  // Clear out all the HTML in the #hs-embed-content container
  const embedContainer = document.getElementById('hs-embed-content');
  if (embedContainer) {
    embedContainer.innerHTML = '';
  } else {
    console.error('#hs-embed-content container does not exist. Recreating the editor container.');
    // Fallback to recreate a new #hs-embed-content container so the app still runs
    const newEmbedContainer = document.createElement('div');
    newEmbedContainer.id = 'hs-embed-content';
    document.body.appendChild(newEmbedContainer);
  }
};

/**
 * This function takes the { html } string and replaces the whole app html with it
 * this would remove all the functionality inside content-embed-ui and replace it with
 * just the html provided. Example use case: Previewing how email would look like if opened
 * in the browser, replacing html is useful in this scenario because we don't want any other
 * affecting html and just want to display the static email
 */
export const replaceAppWithPayloadHTMl = (data = {}) => {
  document.documentElement.innerHTML = data.html;
};

/**
 * This handler takes the { html } string, parses out & clones all script tags
 * and then appends all new elements to the #hs-embed-content template container.
 * It executes a callback if applicable.
 */
export const handleSetIframeContent = (data = {}, callback, embedOptions = {}) => {
  // First cleanup the DOM cruft
  cleanupDOMCruft();
  const {
    includeHeadElements
  } = embedOptions;
  const {
    html
  } = data;
  const doc = Parser.parseFromString(html, 'text/html');
  const scripts = doc.querySelectorAll('script');
  const newScripts = [];
  const externalScriptsLoaded = [];
  const inlineScriptSetters = [];
  // Extract every script tag from the embedded content and
  // create new script elements so they can execute on load
  Array.from(scripts).forEach(script => {
    if (script.parentElement) {
      script.parentElement.removeChild(script);
    }
    const s = document.createElement('script');
    s.async = false;
    s.type = 'text/javascript';
    if (script.src) {
      externalScriptsLoaded.push(new Promise(resolve => {
        s.onload = function () {
          resolve();
        };
      }));
      s.async = false;
      s.src = script.src;
    } else {
      inlineScriptSetters.push(() => {
        s.textContent = script.textContent;
      });
    }
    newScripts.push(s);
  });

  // Append new body and script elements to the #hs-embed-content container
  const embedContainer = document.getElementById('hs-embed-content');
  Array.from(doc.body.childNodes).forEach(el => {
    embedContainer.appendChild(el);
  });
  if (includeHeadElements) {
    const headElements = doc.head.querySelectorAll('style, link[rel="stylesheet"]');
    Array.from(headElements).forEach(el => {
      document.head.appendChild(el);
    });
  }
  newScripts.forEach(el => {
    embedContainer.appendChild(el);
  });
  return Promise.all(externalScriptsLoaded).then(() => {
    inlineScriptSetters.forEach(setter => setter());
    if (callback) {
      callback();
    }
  }).catch(() => {
    throw new Error('[content-embed-ui] error occurred while loading embedded script paths.');
  });
};

/**
 * This function takes the { html } string, extracts all script tags with the
 * `data-execute` attribute, and appends them to the document to ensure they execute
 * when replaceAppWithPayloadHTMl is called. This is necessary because <script> tags
 * added via innerHTML do not automatically execute. Useful for previewing emails or
 * other HTML content with dynamic scripts in a browser. Example use case: Previewing
 * how email would look like when dark mode feature is enabled.
 * It executes a callback if applicable.
 */
export const processAndExecuteScripts = (data = {}, callback) => {
  const {
    html
  } = data;
  const doc = Parser.parseFromString(html, 'text/html');
  const scripts = doc.querySelectorAll('script[data-execute]');
  const newScripts = [];
  const externalScriptsLoaded = [];
  const inlineScriptSetters = [];
  Array.from(scripts).forEach(script => {
    if (script.parentElement) {
      script.parentElement.removeChild(script);
    }
    const s = document.createElement('script');
    s.async = false;
    s.type = 'text/javascript';
    if (script.src) {
      externalScriptsLoaded.push(new Promise(resolve => {
        s.onload = function () {
          resolve();
        };
      }));
      s.src = script.src;
    } else {
      inlineScriptSetters.push(() => {
        s.textContent = script.textContent;
      });
    }
    newScripts.push(s);
  });
  newScripts.forEach(el => {
    document.head.appendChild(el);
  });
  return Promise.all(externalScriptsLoaded).then(() => {
    inlineScriptSetters.forEach(setter => setter());
    if (callback) {
      callback();
    }
  }).catch(() => {
    throw new Error('[content-embed-ui] error occurred while loading script paths.');
  });
};